import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { isString } from "es-toolkit";
import { useTranslator } from "@templafy/translation";
import { useUUID } from "../../hooks/useUUID.js";
import { useSynchronizedProps } from "../form/internal/FieldContext.js";
import { BaseTextInput } from "./internal/BaseTextInput.js";
import { VisuallyHidden } from "../visuallyHidden/VisuallyHidden.js";
import { combineNullableStrings } from "../../utilities/internal/combineNullableStrings.js";
import { ensureArray } from "../../utilities/internal/ensureArray.js";
import { useDevelopmentEffect } from "../../hooks/_internal/useDevelopmentEffect.js";
const TextInput = /*#__PURE__*/ forwardRef(({ testId, className, style, onChange, disabled: _disabled, readOnly: _readOnly, required: _required, size: _size, status: _status, prefix, suffix, 'aria-describedby': ariaDescribedBy, ...props }, ref)=>{
    const prefixId = useUUID({
        prefix: 'prefix'
    });
    const suffixId = useUUID({
        prefix: 'suffix'
    });
    const { disabled, readOnly, required, size, status } = useSynchronizedProps({
        disabled: _disabled,
        readOnly: _readOnly,
        required: _required,
        size: _size,
        status: _status,
        inputType: 'text'
    });
    const prefixArray = ensureArray(prefix).filter(Boolean);
    const hasTextPrefix = prefixArray.some((prefix)=>isString(prefix));
    const suffixArray = ensureArray(suffix).filter(Boolean);
    const hasTextSuffix = suffixArray.some((prefix)=>isString(prefix));
    useDevelopmentEffect(()=>{
        const textPrefixes = ensureArray(prefix).filter((p)=>isString(p));
        const textSuffix = ensureArray(suffix).filter((s)=>isString(s));
        if (textPrefixes.length > 1) console.error('[TextInput] Maximum 1 text prefix is allowed. Using multiple text prefixes will break accessibility.');
        if (textSuffix.length > 1) console.error('[TextInput] Maximum 1 text suffix is allowed. Using multiple text suffixes will break accessibility.');
    }, [
        prefix,
        suffix
    ]);
    return /*#__PURE__*/ jsx(BaseTextInput, {
        wrapperProps: {
            testId,
            className,
            style
        },
        onValueChange: onChange,
        disabled: disabled,
        readOnly: readOnly,
        required: required,
        size: size,
        status: status,
        startAdornment: prefixArray.map((prefix)=>/*#__PURE__*/ jsx(AdornmentComponent, {
                id: hasTextPrefix ? prefixId : void 0,
                position: "prefix",
                disabled: disabled || readOnly,
                children: prefix
            }, isString(prefix) ? prefix : prefix.displayName)),
        endAdornment: suffixArray.map((suffix)=>/*#__PURE__*/ jsx(AdornmentComponent, {
                id: hasTextSuffix ? suffixId : void 0,
                position: "suffix",
                disabled: disabled || readOnly,
                children: suffix
            }, isString(suffix) ? suffix : suffix.displayName)),
        "aria-describedby": combineNullableStrings(ariaDescribedBy, hasTextPrefix && prefixId, hasTextSuffix && suffixId),
        ...props,
        ref: ref
    });
});
TextInput.displayName = 'TextInput';
const AdornmentComponent = ({ id, position, disabled, children })=>{
    const translator = useTranslator();
    return isString(children) ? /*#__PURE__*/ jsxs(BaseTextInput.AdornmentText, {
        id: id,
        children: [
            /*#__PURE__*/ jsxs(VisuallyHidden, {
                children: [
                    translator.uiComponentsTextAdornmentPositionDescription({
                        position
                    }),
                    ' '
                ]
            }),
            children
        ]
    }) : /*#__PURE__*/ jsx(BaseTextInput.AdornmentIcon, {
        disabled: disabled,
        icon: children
    });
};
export { TextInput };
